import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'convex/react'
import {
  Alert,
  Button,
  Card,
  Center,
  Container,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import {
  IconAlertCircle,
  IconCheck,
  IconUsers,
  IconX,
} from '@tabler/icons-react'
import { useAuth } from '@clerk/tanstack-react-start'
import { api } from '../../convex/_generated/api'

export const Route = createFileRoute('/join')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || '',
  }),
  component: JoinPage,
})

function JoinPage() {
  const { token } = Route.useSearch()
  const navigate = useNavigate()
  const { isSignedIn, isLoaded } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // Query invitation details
  const invitation = useQuery(
    api.invitations.getInvitationByToken,
    token ? { token } : 'skip',
  )

  // Mutation to set pending invitation on profile
  const setPendingInvitation = useMutation(
    api.invitations.setPendingInvitationFromToken,
  )

  // Handle the invitation flow
  useEffect(() => {
    const processInvitation = async () => {
      if (!isLoaded) return
      if (!token) {
        setError('No invitation token provided')
        return
      }

      // If not signed in, redirect to sign-in with return URL
      if (!isSignedIn) {
        // User needs to sign in first - redirect to sign-in page
        // After signing in, they'll be redirected back here
        window.location.href = `/sign-in?redirect_url=${encodeURIComponent(`/join?token=${token}`)}`
        return
      }

      // User is signed in - process the invitation
      if (invitation === undefined) {
        // Still loading
        return
      }

      if (invitation === null) {
        setError('Invitation not found or has expired')
        return
      }

      if (invitation.status === 'expired') {
        setError('This invitation has expired')
        return
      }

      if (invitation.status !== 'pending') {
        setError('This invitation is no longer valid')
        return
      }

      // Set the pending invitation on the user's profile
      setIsProcessing(true)
      try {
        await setPendingInvitation({ token })
        // Redirect to dashboard - the AcceptInvitationModal will appear
        navigate({ to: '/dashboard' })
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to process invitation',
        )
        setIsProcessing(false)
      }
    }

    processInvitation()
  }, [isLoaded, isSignedIn, token, invitation, setPendingInvitation, navigate])

  // Loading state
  if (!isLoaded || invitation === undefined || isProcessing) {
    return (
      <Container size="xs" py="xl">
        <Center>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Processing invitation...</Text>
          </Stack>
        </Center>
      </Container>
    )
  }

  // Error state
  if (error) {
    return (
      <Container size="sm" py="xl">
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack align="center" gap="md">
            <IconX size={48} color="var(--mantine-color-red-6)" />
            <Title order={3}>Invitation Error</Title>
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              title="Unable to Process Invitation"
            >
              {error}
            </Alert>
            <Button onClick={() => navigate({ to: '/' })} variant="light">
              Go to Home
            </Button>
          </Stack>
        </Card>
      </Container>
    )
  }

  // Show invitation details (brief moment before redirect)
  if (invitation && invitation.status === 'pending') {
    return (
      <Container size="sm" py="xl">
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack align="center" gap="md">
            <IconUsers size={48} color="var(--mantine-color-blue-6)" />
            <Title order={3}>Group Invitation</Title>
            <Text ta="center">
              You've been invited to join{' '}
              <Text span fw={600}>
                {invitation.groupName || 'a group'}
              </Text>
            </Text>
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Redirecting...
            </Text>
          </Stack>
        </Card>
      </Container>
    )
  }

  return null
}
